import { MonthNames } from './constants/calendar'
import moment from 'moment'

export const createActionCreator = (actionType, { noPayload = false } = {}) => {
  if (noPayload) {
    return () => ({ type: actionType })
  } else {
    return payload => ({ type: actionType, payload })
  }
}

export const constructURI = (path, queryParams = {}) =>
  path +
  (path.endsWith('?') ? '' : '?') +
  Object.entries(queryParams)
    .filter(entry => typeof entry[1] !== 'undefined')
    .map(entry => `${entry[0]}=${encodeURIComponent(entry[1])}`)
    .join('&')

export const mapRange = (range, iteratee) => {
  range = Number(range)

  if (isNaN(range) || !isFinite(range)) {
    throw new TypeError(range)
  }

  let results = []

  for (let i = 0; i < range; i++) {
    results.push(iteratee.call(null, i))
  }

  return results
}

/**
 * Parse a date string in the format: YYYY-MM-DD, this is in accordance with
 * the ISO standard format. Returns null if the string is not a valid date.
 *
 * Note: this function also allows a single digit for month and/or day, so
 * 2010-01-01 is equivalent to 2010-1-1.
 */
export const parseISODate = value => {
  let groups = /^([\d]{4})-([\d]{1,2})-([\d]{1,2})$/.exec(value)

  if (groups !== null) {
    let year = Number(groups[1])
    let month = Number(groups[2]) - 1
    let day = Number(groups[3])

    return new Date(year, month, day)
  }
  return null
}

/**
 * Normalize a date value into a Date object. Accepts ISO format strings, UTC
 * Number timestamps, and Date objects. May return null.
 */
export const toDate = value => {
  if (typeof value === 'string') {
    return parseISODate(value)
  }
  if (typeof value === 'number') {
    return isNaN(value) ? null : new Date(value)
  }
  return !value ? null : value
}

export const serializeISODate = date => {
  if (date && date.toISOString) {
    return date.toISOString().replace(/T.*$/, '')
  }
  return ''
}

export const today = ({ years, months, days } = {}) => {
  let now = new Date(Date.now())

  const orZero = v => (typeof v === 'undefined' ? 0 : v)

  return new Date(
    now.getFullYear() + orZero(years),
    now.getMonth() + orZero(months),
    now.getDate() + orZero(days)
  )
}

export const toOrdinal = v => {
  const n = Number(v)
  const i = n % 10
  const d = n % 100

  if (n !== n) {
    return '' // NaN
  } else if (i === 1 && d !== 11) {
    return `${n}st`
  } else if (i === 2 && d !== 12) {
    return `${n}nd`
  } else if (i === 3 && d !== 13) {
    return `${n}rd`
  }
  return `${n}th`
}

export const formatDate = date => {
  const year = date.getFullYear()
  const month = MonthNames[date.getMonth()]
  const day = toOrdinal(date.getDate())

  return `${month} ${day}, ${year}`
}

export const reversed = values =>
  Array.prototype.map.call(values, v => v).reverse()

/**
 * Fast fuzzy search function. Tests if needle roughly matches haystack.
 *
 * @param needle
 * @param haystack
 * @returns {boolean}
 */
export const fuzzySearch = (needle, haystack) => {
  const hlen = haystack.length
  const nlen = needle.length
  if (nlen > hlen) {
    return false
  }
  if (nlen === hlen) {
    return needle === haystack
  }
  haystack = haystack.toLowerCase()
  outer: for (let i = 0, j = 0; i < nlen; i++) {
    let nch = needle.charCodeAt(i)
    while (j < hlen) {
      if (haystack.charCodeAt(j++) === nch) {
        continue outer
      }
    }
    return false
  }
  return true
}

export const calculateCountdown = endDate => {
  let diff = endDate.diff(moment(), 'seconds')

  if (diff <= 0) return false
  const timeLeft = {
    years: 0,
    days: 0,
    hours: 0,
    min: 0,
  }

  if (diff >= 365.25 * 24 * 60 * 60) {
    timeLeft.years = Math.floor(diff / (365.25 * 86400))
    diff -= timeLeft.years * 365.25 * 86400
  }
  if (diff >= 24 * 60 * 60) {
    timeLeft.days = Math.floor(diff / 86400)
    diff -= timeLeft.days * 86400
  }
  if (diff >= 60 * 60) {
    timeLeft.hours = Math.floor(diff / 3600)
    diff -= timeLeft.hours * 3600
  }
  if (diff >= 60) {
    timeLeft.min = Math.floor(diff / 60)
    diff -= timeLeft.min * 60
  }

  return timeLeft
}

/* eslint-disable */
export const debounce = (func, wait, immediate) => {
  let timeout
  return function() {
    const context = this
    const args = arguments
    const later = function() {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}
